import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, first } from 'rxjs/operators';
import { Angulartics2GoogleAnalytics } from 'angulartics2/ga';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { environment } from '@env/environment';
import { Logger, UntilDestroy, untilDestroyed } from '@core';
import { I18nService } from '@app/i18n';
import { Keepalive } from '@ng-idle/keepalive';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { SessionComponent } from './@shared/dialogs/session/session.component';
import { AuthenticationService, CredentialsService } from './login';
import { Credentials } from './@core/models/login-models';
import { LOGIN } from './@core/constants/routes.constants';
import { MultilevelMenuService, MultilevelNodes } from 'ng-material-multilevel-menu';
import { Location } from '@angular/common';

const log = new Logger('App');

const URL_STORE = [
  {
    url: '/requests/request-show-edit/',
    redirectTo: ['/requests/panel', '/users/search-operation'],
  },
  { url: '/users/detail-user', redirectTo: ['/users/search'] },
  {
    url: '/requests/participants-list/',
    redirectTo: ['/requests/panel', '/users/search-operation'],
  },
  {
    url: '/requests/logalty-signature',
    redirectTo: ['/requests/panel', '/users/search-operation'],
  },
  {
    url: '/requests/logalty-consents',
    redirectTo: ['/requests/panel', '/users/search-operation'],
  },
  {
    url: '/requests/update-documentation/',
    redirectTo: ['/requests/panel', '/users/search-operation'],
  },
];

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  idleState = 'NOT_STARTED';
  timedOut = false;
  lastPing?: Date = null;
  title = 'angular-idle-timeout';
  sessionPopup: MatDialogRef<any>;
  currentURL: string;
  dateExpired: Date;
  infoToken: any;
  tabChange: boolean = false;
  countdownPopUp: number;
  private menus: MultilevelNodes[] = [];
  private lastSelectedMenu = { prevUrl: '', parentIndex: -1, childrenIndex: -1 };
  private urlsStored = URL_STORE;
  active: boolean = false;

  private storageEventListener: EventListener;
  private previousUser: string;
  private login: boolean = false;

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private idle: Idle,
    private keepalive: Keepalive,
    private matDialog: MatDialog,
    private credentialsService: CredentialsService,
    private authenticationService: AuthenticationService,
    // do not remove the analytics injection, even if the call in ngOnInit() is removed
    // this injection initializes page tracking through the router
    private angulartics2GoogleAnalytics: Angulartics2GoogleAnalytics,
    private i18nService: I18nService,
    private multilevelMenuService: MultilevelMenuService,
    private location: Location
  ) {
    console.log('Environment: ' + environment.name);
    idle.setIdle(environment.sessionTimeout);
    idle.setTimeout(environment.windowSesionTimeout);
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.currentURL = this.router.url;

    idle.onIdleEnd.subscribe(() => {
      this.idleState = 'NO_LONGER_IDLE';
    });

    idle.onTimeout.subscribe(() => {
      this.idleState = 'TIMED_OUT';
      this.timedOut = true;
      this.closeModalSession();
      this.closeSession();
    });

    idle.onIdleStart.subscribe(() => {
      this.idleState = 'IDLE_START';
      this.openModalSession();
      idle.clearInterrupts();
    });

    idle.onTimeoutWarning.subscribe((countdown) => {
      this.countdownPopUp = countdown;
      this.idleState = 'You will time out in ' + countdown + ' seconds!';
      this.sessionPopup.componentInstance.count = Math.floor((countdown - 1) / 60) + 1;
      this.sessionPopup.componentInstance.progressCount = this.reverseNumber(countdown);
      this.sessionPopup.componentInstance.countMinutes = Math.floor(countdown / 60);
      this.sessionPopup.componentInstance.countSeconds = countdown % 60;
    });

    // sets the ping interval to 15 seconds
    this.keepalive.interval(15);

    // si ya esta autenticado y se mantiene la sesion
    if (this.credentialsService.isAuthenticated() && this.currentURL !== '/') {
      const credentials: Credentials = JSON.parse(localStorage.getItem('credentials'));
      this.authenticationService.startRefreshTokenTimer(credentials.loginResponse);
    }

    this.credentialsService.getUserLoggedIn().subscribe((userLoggedIn) => {
      if (userLoggedIn) {
        idle.watch();
        this.timedOut = false;
      } else {
        idle.stop();
      }
    });
  }

  @HostListener('document:visibilitychange', ['$event'])
  visibilitychange() {
    this.tabChange = !this.tabChange;

    if (this.tabChange) {
      if (this.credentialsService.isAuthenticated()) {
        this.authenticationService.sendRefreshToken({ isChangeTab: true });
        localStorage.setItem('TimeBeforeGo', Date.now().toString());
      }
    } else {
      let totalTime: number = parseInt(localStorage.getItem('TimeBeforeGo')) + 300000;
      if (totalTime > Date.now() && this.credentialsService.isAuthenticated()) {
        if (this.active) {
          this.authenticationService.sendRefreshToken({ isChangeTab: true });
          this.authenticationService.stopRefreshTokenTimer();
          this.idle.stop();
          this.matDialog.closeAll();
          this.idle.setIdle(1);
          if (this.countdownPopUp) {
            this.idle.setTimeout(this.countdownPopUp);
          }
          this.idle.watch();
          this.active = false;
        }
      } else if (
        !this.location.path().includes('/logalty-signature-async-merchant/') &&
        !this.location.path().includes('/logalty-signature-async/') &&
        !this.location.path().includes('/logalty-signature-thirdParty/') &&
        !this.location.path().includes('/landing-videoselfing') &&
        !this.location.path().includes('/landing-finish-videoselfing') &&
        !this.location.path().includes('/landing-onfido-videoselfing') &&
        !this.location.path().includes('/cookies-policy') &&
        !this.location.path().includes('/password-recovery/')
      ) {
        this.closeSession();
      }
    }
  }

  ngOnInit() {
    //Si usuario cambia, actualizar todas las pestañas
    this.storageEventListener = (event: StorageEvent) => {
      if (localStorage.getItem('credentials') != null) {
        //Evita recargar de manera indefinida tras logout
        if (sessionStorage.getItem('0') != localStorage.getItem('credentials')) {
          sessionStorage.setItem('0', localStorage.getItem('credentials'));
          location.reload();
        }
      }
    };

    window.addEventListener('storage', this.storageEventListener);

    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    log.debug('init');
    this.angulartics2GoogleAnalytics.startTracking();
    this.angulartics2GoogleAnalytics.eventTrack(environment.version, { category: 'App initialized' });

    // Setup translations
    this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);
    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe(() => this.i18nService.updateTitle());
    this.authenticationService
      .getMenuShell()
      .pipe(
        filter((menus) => menus !== this.menus),
        untilDestroyed(this)
      )
      .subscribe((menus) => {
        this.menus = menus;
        if (this.menus) {
          const id =
            this.lastSelectedMenu.childrenIndex >= 0
              ? this.menus[this.lastSelectedMenu.parentIndex]?.items[this.lastSelectedMenu.childrenIndex]?.id
              : this.menus[this.lastSelectedMenu.parentIndex]?.id;
          this.multilevelMenuService.selectMenuByID(id);
        }
      });
    const onNavigationEnd = this.router.events;
    onNavigationEnd
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe((nav: any) => this.navigationHandler(nav?.url));
  }

  openModalSession() {
    this.active = true;
    this.sessionPopup = this.matDialog.open(SessionComponent);
    this.sessionPopup
      .afterClosed()
      .pipe(first())
      .subscribe((result: any) => {
        if (result !== '' && 'logout' === result) {
          this.closeSession();
        } else if (result !== '' && 'refresh' === result) {
          this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
          this.continue();
          this.reset();
        }
      });
  }

  closeModalSession(): void {
    if (this.sessionPopup) {
      this.sessionPopup.close();
    }
  }

  reverseNumber(countdown: number): number {
    return ((environment.windowSesionTimeout - (countdown - 1)) * 100) / environment.windowSesionTimeout;
  }

  private resetDefaultIdle() {
    this.idle.setIdle(environment.sessionTimeout);
    this.idle.setTimeout(environment.windowSesionTimeout);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.watch();
    this.countdownPopUp = environment.windowSesionTimeout;
    this.active = false;
  }
  reset() {
    this.resetDefaultIdle();
    this.idleState = 'Started.';
    this.timedOut = false;
  }

  continue() {
    const credentials: Credentials = JSON.parse(localStorage.getItem('credentials'));
    console.log(credentials);
    if (credentials) {
      this.closeModalSession();
      // el token se refresca automaticamente por detras, al continuar sesión se cierra el modal
      // this.authenticationService.refreshToken(credentials.JWT_TOKEN).subscribe(
      //   () => {
      //     console.log('token refresh');
      //
      //   },
      //   (error) => {
      //     console.log(error);
      //     this.closeSession();
      //   }
      // );
    }
  }

  closeSession() {
    this.authenticationService.logout().subscribe(
      () => {},
      (error) => {
        console.log(error);
      }
    );
    this.logoutUser();
  }

  logoutUser() {
    this.credentialsService.setCredentials();
    this.authenticationService.stopRefreshTokenTimer();
    this.idle.stop();
    this.resetDefaultIdle();
    this.matDialog.closeAll();
    this.router.navigate([LOGIN], { replaceUrl: true });
  }

  ngOnDestroy() {
    this.i18nService.destroy();
  }

  private navigationHandler(navigationUrl: string) {
    console.log('User navigate to: ', navigationUrl);
    if (navigationUrl) {
      let indexChildren = -1;
      let indexMenu = -1;
      if (this.menus && this.menus.length > 0) {
        //Limpieza de URL
        let url = navigationUrl.trim();
        if (url.includes(';')) {
          //Clean Keep Filter
          url = url.slice(0, url.lastIndexOf(';'));
        }
        //Búsqueda del índice de URL
        indexMenu = this.menus.findIndex((menu) => {
          if (menu?.items) {
            //Búsqueda del índice en los hijos
            indexChildren = menu.items.findIndex((items) => items.link === url);
            return indexChildren >= 0;
          } else {
            return menu.link === url;
          }
        });
        if (indexMenu >= 0) {
          //Guardado URL en caso de paso previo a una URL que no existe en la carga del menú (shell)
          if (
            this.urlsStored.find(
              (urlStored) => urlStored.redirectTo?.filter((urlRedirection) => url.includes(urlRedirection)).length > 0
            )
          ) {
            this.lastSelectedMenu.prevUrl = url;
          }
        } else {
          //Utilización de la URL guardada en el paso previo
          if (this.urlsStored.filter((urlRedirection) => url.includes(urlRedirection.url)).length > 0) {
            console.log('Use stored URL', this.lastSelectedMenu);
            url = this.lastSelectedMenu.prevUrl;
            indexMenu = this.lastSelectedMenu.parentIndex;
            indexChildren = this.lastSelectedMenu.childrenIndex;
          }
        }
      } else {
        //DEFAULT HOME
        if (this.credentialsService.isAuthenticated()) {
          indexChildren = -1;
          indexMenu = 0;
        }
      }
      this.lastSelectedMenu = {
        parentIndex: indexMenu,
        childrenIndex: indexChildren,
        prevUrl: this.lastSelectedMenu.prevUrl,
      };
      console.log('Save menu', this.lastSelectedMenu);
    }
  }
}
